import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { userAtom } from '../store/atoms';
import { notificationKeys } from './useNotifications';
import { mentorshipRequestKeys } from './useMentorshipRequests';
import { pulseKeys } from './usePulse';
import { retroKeys } from './useRetros';
import type { PulseCheck } from '../../../shared/types';
import type {
  RetroCardDto,
  RetroCardDeletedEvent,
  RetroCardVotedEvent,
  RetroPresenceEvent,
  RetroConcludedEvent,
  RetroActionItemsEvent,
  RetroTimerEvent,
  RetroBoardDto,
} from '../../../shared/types/retro';


/**
 * Opens a persistent SSE connection to /api/c/:slug/events.
 * Handles:
 *   - "notification"   → invalidate notification queries (badge count + list)
 *   - "insight_update" → update cached PulseCheck.insightStatus in place;
 *                        fetch insight when status becomes "ready"
 *
 * Mount this once at the CrewWrapper level. The browser auto-reconnects on error.
 */
export function useSse(crewSlug: string) {
    const user = useAtomValue(userAtom);
    const queryClient = useQueryClient();

    useEffect(() => {
        if (!crewSlug || !user) return;

        const es = new EventSource(`/api/c/${crewSlug}/events`);

        es.addEventListener('notification', () => {
            // Invalidate both unread count and notification list
            queryClient.invalidateQueries({ queryKey: notificationKeys.crew(crewSlug) });
        });

        es.addEventListener('mentorship_request_updated', () => {
            queryClient.invalidateQueries({ queryKey: mentorshipRequestKeys.crews(crewSlug) });
        });

        es.addEventListener('insight_update', (e: MessageEvent) => {
            const { sessionId, insightStatus } = JSON.parse(e.data) as {
                sessionId: string;
                insightStatus: string;
            };

            // Update the cached session in place — no round-trip needed
            queryClient.setQueryData(
                pulseKeys.session(crewSlug, sessionId),
                (old: PulseCheck | null) => old ? { ...old, insightStatus } : old
            );

            // Fetch the insight document once it's ready
            if (insightStatus === 'ready') {
                queryClient.invalidateQueries({ queryKey: pulseKeys.insight(crewSlug, sessionId) });
            }
        });

        // ── Retro events ───────────────────────────────────────────────────

        es.addEventListener('retro_card_added', (e: MessageEvent) => {
            const card = JSON.parse(e.data) as RetroCardDto;
            queryClient.setQueryData<RetroBoardDto>(
                retroKeys.board(crewSlug, card.retroId),
                old => {
                    if (!old) return old;
                    const exists = old.cards.some(c => c._id === card._id);
                    if (exists) return old;
                    return { ...old, cards: [...old.cards, card] };
                }
            );
        });

        es.addEventListener('retro_card_updated', (e: MessageEvent) => {
            const card = JSON.parse(e.data) as RetroCardDto;
            queryClient.setQueryData<RetroBoardDto>(
                retroKeys.board(crewSlug, card.retroId),
                old => {
                    if (!old) return old;
                    return { ...old, cards: old.cards.map(c => c._id === card._id ? card : c) };
                }
            );
        });

        es.addEventListener('retro_card_deleted', (e: MessageEvent) => {
            const { retroId, cardId } = JSON.parse(e.data) as RetroCardDeletedEvent;
            queryClient.setQueryData<RetroBoardDto>(
                retroKeys.board(crewSlug, retroId),
                old => {
                    if (!old) return old;
                    return { ...old, cards: old.cards.filter(c => c._id !== cardId) };
                }
            );
        });

        es.addEventListener('retro_card_voted', (e: MessageEvent) => {
            const { retroId, cardId, upvotes, downvotes } = JSON.parse(e.data) as RetroCardVotedEvent;
            queryClient.setQueryData<RetroBoardDto>(
                retroKeys.board(crewSlug, retroId),
                old => {
                    if (!old) return old;
                    return {
                        ...old,
                        cards: old.cards.map(c =>
                            c._id === cardId ? { ...c, upvotes, downvotes } : c
                        ),
                    };
                }
            );
        });

        es.addEventListener('retro_card_commented', (e: MessageEvent) => {
            const { retroId, cardId, comment } = JSON.parse(e.data) as {
                retroId: string;
                cardId: string;
                comment: RetroCardDto['comments'][number];
            };
            queryClient.setQueryData<RetroBoardDto>(
                retroKeys.board(crewSlug, retroId),
                old => {
                    if (!old) return old;
                    return {
                        ...old,
                        cards: old.cards.map(c =>
                            c._id === cardId
                                ? { ...c, comments: [...c.comments, comment] }
                                : c
                        ),
                    };
                }
            );
        });

        es.addEventListener('retro_presence', (e: MessageEvent) => {
            const { retroId, participants } = JSON.parse(e.data) as RetroPresenceEvent;
            queryClient.setQueryData<RetroBoardDto>(
                retroKeys.board(crewSlug, retroId),
                old => {
                    if (!old) return old;
                    return { ...old, retro: { ...old.retro, participants } };
                }
            );
        });

        es.addEventListener('retro_concluded', (e: MessageEvent) => {
            const { retroId, concludedAt } = JSON.parse(e.data) as RetroConcludedEvent;
            queryClient.setQueryData<RetroBoardDto>(
                retroKeys.board(crewSlug, retroId),
                old => {
                    if (!old) return old;
                    return { ...old, retro: { ...old.retro, status: 'concluded', concludedAt } };
                }
            );
            // Also invalidate the list so the history view updates
            queryClient.invalidateQueries({ queryKey: retroKeys.crew(crewSlug) });
        });

        es.addEventListener('retro_action_updated', (e: MessageEvent) => {
            const { retroId, actionItems } = JSON.parse(e.data) as RetroActionItemsEvent;
            queryClient.setQueryData<RetroBoardDto>(
                retroKeys.board(crewSlug, retroId),
                old => {
                    if (!old) return old;
                    return { ...old, retro: { ...old.retro, actionItems } };
                }
            );
        });

        es.addEventListener('retro_timer', (e: MessageEvent) => {
            const state = JSON.parse(e.data) as RetroTimerEvent;
            queryClient.setQueryData<RetroBoardDto>(
                retroKeys.board(crewSlug, state.retroId),
                old => old ? { ...old, timer: state } : old
            );
        });

        // Browser auto-reconnects on error; no manual handling needed
        es.onerror = () => { /* noop */ };

        return () => {
            es.close();
        };
    }, [crewSlug, user, queryClient]);
}
